import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'

import type { PublicBranchInfo } from '@/bindings/PublicBranchInfo'
import type { PublicRestaurant } from '@/bindings/PublicRestaurant'
import { Card, Page } from '@/components/layout'
import { branchHome } from '@/features/back-office/branch-home'
import { LandingFeedbackButtons } from '@/features/feedback/landing-buttons'
import { branchPageQuery, restaurantPath } from '@/features/landing/api'
import { Intro } from '@/features/landing/restaurant-page'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerLoadError, CustomerLoading, CustomerShell } from '@/features/ordering/customer-chrome'
import { MenuList } from '@/features/ordering/menu-list'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useT } from '@/lib/i18n/translate'
import { formatOpeningHours } from '@/lib/opening-hours'
import { canManageBranch } from '@/lib/roles'

const ROUTE = '/$restaurantSlug/$branchSlug'

/**
 * `/<restaurant-slug>/<branch-slug>` (PRD "Branch landing page"): the intro,
 * the reference menu as this branch sells it (F4 overlay, read-only), the way
 * to order, and for anyone with a role here the way into their pages —
 * admin and above to branch management, staff to the queue. The announcement,
 * hours, address and phone are what the branch info page (H2) writes.
 */
export function BranchLandingPage() {
  const { restaurantSlug, branchSlug } = useParams({ from: ROUTE })
  const t = useT()
  const auth = useCustomerAuth()
  const page = useQuery(branchPageQuery(restaurantSlug, branchSlug))

  if (page.isPending) return <CustomerLoading />
  if (page.error) return <CustomerLoadError error={page.error} notFound={t('landing.notFound')} retry={() => void page.refetch()} />

  const { restaurant, branch, info, categories, items, my_role } = page.data
  const params = { restaurantSlug, branchSlug }
  return (
    <CustomerShell color={restaurant.color}>
      <CustomerHeader restaurant={restaurant.name} branch={branch.name} auth={auth} />
      <Page width="md" className="flex flex-1 flex-col gap-4">
        <Link to={restaurantPath(restaurantSlug)} className="text-accent-ink self-start text-sm font-medium hover:underline">
          ← {t('landing.allBranches', { name: restaurant.name })}
        </Link>
        {my_role && (
          <Link to={branchHome(my_role)} params={params} className={CTA}>
            {t(canManageBranch(my_role) ? 'landing.manageBranch' : 'landing.openQueue')}
          </Link>
        )}
        <Announcement restaurant={restaurant} info={info} />
        <Intro restaurant={restaurant} />
        <BranchDetails info={info} />
        <section aria-labelledby="menu-heading" className="flex flex-col gap-1">
          <h2 id="menu-heading" className="text-lg font-semibold">
            {t('landing.menuTitle')}
          </h2>
          <p className="text-muted text-sm">{t('landing.menuHint')}</p>
          <MenuList
            categories={categories}
            items={items}
            view={{
              main: restaurant.main_language,
              currency: branch.currency,
              readOnly: true,
            }}
          />
        </section>
        <LandingFeedbackButtons auth={auth} restaurant={restaurant} branch={branch} />
      </Page>
    </CustomerShell>
  )
}

/** PRD show-filled rule: the T4.3 chain picks the viewer's language, else whichever is filled. */
function Announcement({ restaurant, info }: { restaurant: PublicRestaurant; info: PublicBranchInfo }) {
  const t = useT()
  const text = useLocalizedText(restaurant.main_language)(info.announcement)
  if (!text) return null
  return (
    <Card role="note" aria-label={t('landing.announcementTitle')} className="border-accent flex flex-col gap-1 p-5">
      <h2 className="text-accent-ink text-sm font-semibold uppercase">{t('landing.announcementTitle')}</h2>
      <p className="whitespace-pre-line">{text}</p>
    </Card>
  )
}

/** Hours (day names in the viewer's language), address, phone; nothing when the branch wrote none. */
function BranchDetails({ info }: { info: PublicBranchInfo }) {
  const t = useT()
  const hours = formatOpeningHours(info.opening_hours, t)
  if (hours.length === 0 && !info.address && !info.phone) return null
  return (
    <Card className="p-5">
      <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
        {hours.length > 0 && (
          <>
            <dt className="text-muted font-medium">{t('landing.openingHours')}</dt>
            <dd>
              {hours.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </dd>
          </>
        )}
        {info.address && (
          <>
            <dt className="text-muted font-medium">{t('landing.address')}</dt>
            <dd>{info.address}</dd>
          </>
        )}
        {info.phone && (
          <>
            <dt className="text-muted font-medium">{t('landing.phone')}</dt>
            <dd>
              <a href={`tel:${info.phone}`} className="text-accent-ink hover:underline">
                {info.phone}
              </a>
            </dd>
          </>
        )}
      </dl>
    </Card>
  )
}

const CTA =
  'bg-accent text-accent-fg hover:bg-accent-hover min-h-tap flex items-center justify-center rounded-lg px-4 font-medium sm:self-start'
